import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  SlidersHorizontal,
  X,
  Clock3,
  Target,
  Wand2,
  Check,
} from 'lucide-react';
import { useAppSelector } from '@/shared/store/hooks';
import { startFreshCampaignCreate } from '@/features/campaigns/utils/startFreshCampaignCreate';
import { ClientPersonalAdvertiserPrompt } from '@/features/campaigns/components/ClientPersonalAdvertiserPrompt';
import { isClientRole } from '@/shared/config/roles';

type CampaignModeSelectModalProps = {
  open: boolean;
  onClose: () => void;
};

export function CampaignModeSelectModal({
  open,
  onClose,
}: CampaignModeSelectModalProps) {
  const navigate = useNavigate();
  const permissions = useAppSelector((s) => s.auth.permissions);
  const role = useAppSelector((s) => s.auth.user?.role);
  const isClient = isClientRole(role);
  const canAi =
    permissions.length === 0 ||
    permissions.includes('create-campaign-ai') ||
    permissions.includes('campaigns_module_access');

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const pick = (mode: 'ai' | 'standard') => {
    onClose();
    // Manual always starts with the shared schedule default.
    // Per-screen booking is chosen on Step 3 once screens exist.
    startFreshCampaignCreate(navigate, mode, 'random');
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="campaign-mode-title"
    >
      <button
        type="button"
        className="absolute inset-0 border-none bg-[#0F1B2D]/50 backdrop-blur-[2px]"
        aria-label="Close"
        onClick={onClose}
      />

      <div className="camp-fade-up relative z-10 w-full max-w-2xl overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl">
        <div
          className="h-1.5 w-full"
          style={{ background: 'var(--dash-gradient)' }}
        />

        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 z-20 rounded-full p-2 text-[var(--dash-muted)] transition hover:bg-[var(--dash-soft)] hover:text-[var(--dash-ink)]"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        {isClient ? (
          <div className="px-5 pt-7 pb-6 text-left sm:px-8 sm:pt-8 sm:pb-8">
            <ClientPersonalAdvertiserPrompt
              variant="modal"
              onCancel={onClose}
            />
          </div>
        ) : (
          <div className="px-5 pt-7 pb-6 text-left sm:px-8 sm:pt-8 sm:pb-8">
            <p className="m-0 text-[11px] font-semibold tracking-[0.12em] text-[var(--dash-brand)] uppercase">
              New campaign
            </p>
            <h2
              id="campaign-mode-title"
              className="mt-1.5 mb-0 text-2xl font-semibold tracking-tight text-[var(--dash-ink)]"
            >
              How do you want to build?
            </h2>
            <p className="mt-2 mb-6 max-w-lg text-sm leading-relaxed text-[var(--dash-muted)]">
              Pick a path — you can always review screens, slots, and cost before
              you pay.
            </p>

            <div
              className={`grid gap-3 ${canAi ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}
            >
              {canAi ? (
                <button
                  type="button"
                  onClick={() => pick('ai')}
                  className="camp-mode-card camp-mode-card--ai"
                >
                  <span className="absolute top-3 right-3 rounded-full bg-[var(--dash-brand-soft)] px-2 py-0.5 text-[10px] font-bold tracking-wide text-[var(--dash-brand)] uppercase">
                    Faster
                  </span>
                  <div
                    className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl text-white"
                    style={{ background: 'var(--dash-gradient)' }}
                  >
                    <Sparkles className="h-5 w-5" />
                  </div>
                  <h3 className="m-0 text-[17px] font-semibold text-[var(--dash-ink)]">
                    AI Builder
                  </h3>
                  <p className="mt-1 mb-3 text-xs leading-relaxed text-[var(--dash-muted)]">
                    Tell us budget & vision — we draft screens and peak hours.
                  </p>
                  <ul className="m-0 flex list-none flex-col gap-1.5 p-0 text-[12px] text-[var(--dash-ink)]">
                    <Benefit>Ready in ~2 minutes</Benefit>
                    <Benefit>Auto peak-hour slots</Benefit>
                    <Benefit>Budget-aware plan</Benefit>
                  </ul>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-[var(--dash-brand)]">
                    <Wand2 className="h-3.5 w-3.5" />
                    Start with AI
                  </span>
                </button>
              ) : null}

              <button
                type="button"
                onClick={() => pick('standard')}
                className="camp-mode-card"
              >
                <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-[var(--dash-soft)] text-[var(--dash-ink)]">
                  <SlidersHorizontal className="h-5 w-5" />
                </div>
                <h3 className="m-0 text-[17px] font-semibold text-[var(--dash-ink)]">
                  Manual Builder
                </h3>
                <p className="mt-1 mb-3 text-xs leading-relaxed text-[var(--dash-muted)]">
                  Choose every screen, slot, and creative yourself — full control.
                </p>
                <ul className="m-0 flex list-none flex-col gap-1.5 p-0 text-[12px] text-[var(--dash-ink)]">
                  <Benefit>Pick exact screens</Benefit>
                  <Benefit>Shared or per-screen slots</Benefit>
                  <Benefit>4 clear steps</Benefit>
                </ul>
                <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-[var(--dash-ink)]">
                  <Target className="h-3.5 w-3.5" />
                  Build manually
                </span>
              </button>
            </div>

            <p className="mt-5 mb-0 flex items-center justify-center gap-1.5 text-center text-[11px] text-[var(--dash-muted)]">
              <Clock3 className="h-3.5 w-3.5" />
              You can cancel anytime before payment
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function Benefit({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-center gap-2">
      <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[var(--dash-success-soft)] text-[var(--dash-success)]">
        <Check className="h-2.5 w-2.5" strokeWidth={3} />
      </span>
      {children}
    </li>
  );
}
